const cleanText = (value) => String(value ?? "").replace(/\s+/g, " ").trim();
const hasMinimumLocationLength = (value) => cleanText(value).length > 1;

const normalizeKey = (value) => cleanText(value).toLowerCase();

const safeJsonParse = (value, fallback = null) => {
  if (typeof value !== "string") return fallback;
  try {
    return JSON.parse(value);
  } catch {
    return fallback;
  }
};

const COUNTRY_LABEL_BY_KEY = new Map([
  ["us", "USA"], ["u s", "USA"], ["usa", "USA"], ["u s a", "USA"],
  ["united states", "USA"], ["united states of america", "USA"],
  ["uk", "UK"], ["u k", "UK"], ["united kingdom", "UK"], ["great britain", "UK"],
  ["canada", "Canada"],
  ["ireland", "Ireland"],
  ["germany", "Germany"], ["deutschland", "Germany"],
  ["france", "France"],
  ["spain", "Spain"],
  ["italy", "Italy"],
  ["netherlands", "Netherlands"], ["holland", "Netherlands"],
  ["belgium", "Belgium"],
  ["switzerland", "Switzerland"],
  ["sweden", "Sweden"],
  ["norway", "Norway"],
  ["denmark", "Denmark"],
  ["finland", "Finland"],
  ["poland", "Poland"],
  ["portugal", "Portugal"],
  ["austria", "Austria"],
  ["czech republic", "Czech Republic"], ["czechia", "Czech Republic"],
  ["romania", "Romania"],
  ["hungary", "Hungary"],
  ["greece", "Greece"],
  ["turkey", "Turkey"],
  ["australia", "Australia"],
  ["new zealand", "New Zealand"],
  ["japan", "Japan"],
  ["china", "China"],
  ["india", "India"],
  ["singapore", "Singapore"],
  ["hong kong", "Hong Kong"],
  ["south korea", "South Korea"],
  ["philippines", "Philippines"],
  ["indonesia", "Indonesia"],
  ["vietnam", "Vietnam"],
  ["thailand", "Thailand"],
  ["malaysia", "Malaysia"],
  ["mexico", "Mexico"],
  ["brazil", "Brazil"], ["brasil", "Brazil"],
  ["argentina", "Argentina"],
  ["colombia", "Colombia"],
  ["uae", "UAE"], ["united arab emirates", "UAE"],
  ["israel", "Israel"],
  ["saudi arabia", "Saudi Arabia"],
  ["south africa", "South Africa"],
  ["egypt", "Egypt"],
  ["nigeria", "Nigeria"],
]);

const COUNTRY_FULL_LABEL_BY_KEY = new Map([
  ["usa", "United States"],
  ["uk", "United Kingdom"],
  ["canada", "Canada"],
  ["uae", "United Arab Emirates"],
]);

const BARE_LOCATION_COUNTRY_HINT_BY_KEY = new Map([
  ["calgary", "Canada"],
  ["edmonton", "Canada"],
  ["montreal", "Canada"],
  ["orange county", "USA"],
  ["ottawa", "Canada"],
  ["quebec city", "Canada"],
  ["silicon valley", "USA"],
  ["toronto", "Canada"],
  ["vancouver", "Canada"],
  ["winnipeg", "Canada"],
]);

const OTHER_LOCATION_GROUP_LABEL = "Other Locations";
const INVALID_LOCATION_PATTERNS = [
  /\babout\b/i,
  /\bbenefits\b/i,
  /\btestimonial(s)?\b/i,
  /\bblog\b/i,
  /\bcontact\b/i,
  /\bcareer portal\b/i,
  /\bmultiple locations\b/i,
  /\bglobal\b/i,
  /\bacross north america\b/i,
  /\b(?:asia pacific|middle east|europe|africa)\b/i,
  /https?:\/\//i,
  /\bwww\./i,
  /@/,
];

const US_STATE_ENTRIES = [
  ["AL", "Alabama"],
  ["AK", "Alaska"],
  ["AZ", "Arizona"],
  ["AR", "Arkansas"],
  ["CA", "California"],
  ["CO", "Colorado"],
  ["CT", "Connecticut"],
  ["DE", "Delaware"],
  ["FL", "Florida"],
  ["GA", "Georgia"],
  ["HI", "Hawaii"],
  ["ID", "Idaho"],
  ["IL", "Illinois"],
  ["IN", "Indiana"],
  ["IA", "Iowa"],
  ["KS", "Kansas"],
  ["KY", "Kentucky"],
  ["LA", "Louisiana"],
  ["ME", "Maine"],
  ["MD", "Maryland"],
  ["MA", "Massachusetts"],
  ["MI", "Michigan"],
  ["MN", "Minnesota"],
  ["MS", "Mississippi"],
  ["MO", "Missouri"],
  ["MT", "Montana"],
  ["NE", "Nebraska"],
  ["NV", "Nevada"],
  ["NH", "New Hampshire"],
  ["NJ", "New Jersey"],
  ["NM", "New Mexico"],
  ["NY", "New York"],
  ["NC", "North Carolina"],
  ["ND", "North Dakota"],
  ["OH", "Ohio"],
  ["OK", "Oklahoma"],
  ["OR", "Oregon"],
  ["PA", "Pennsylvania"],
  ["RI", "Rhode Island"],
  ["SC", "South Carolina"],
  ["SD", "South Dakota"],
  ["TN", "Tennessee"],
  ["TX", "Texas"],
  ["UT", "Utah"],
  ["VT", "Vermont"],
  ["VA", "Virginia"],
  ["WA", "Washington"],
  ["WV", "West Virginia"],
  ["WI", "Wisconsin"],
  ["WY", "Wyoming"],
  ["DC", "District of Columbia"],
];

const US_STATE_CODE_BY_KEY = new Map(
  US_STATE_ENTRIES.flatMap(([code, name]) => [
    [normalizeKey(code), code],
    [normalizeKey(name), code],
  ])
);

// Canadian province/territory codes → full names (both code and full name map to full name)
const CA_PROVINCE_ENTRIES = [
  ["AB", "Alberta"],
  ["BC", "British Columbia"],
  ["MB", "Manitoba"],
  ["NB", "New Brunswick"],
  ["NL", "Newfoundland and Labrador"],
  ["NS", "Nova Scotia"],
  ["NT", "Northwest Territories"],
  ["NU", "Nunavut"],
  ["ON", "Ontario"],
  ["PE", "Prince Edward Island"],
  ["QC", "Quebec"],
  ["SK", "Saskatchewan"],
  ["YT", "Yukon"],
];

// Maps both code ("on") and full name ("ontario") → full name ("Ontario")
const CA_PROVINCE_FULL_BY_KEY = new Map(
  CA_PROVINCE_ENTRIES.flatMap(([code, name]) => [
    [normalizeKey(code), name],
    [normalizeKey(name), name],
  ])
);

const US_STATE_DEFAULT_CITY_BY_CODE = new Map([
  ["AL", "Montgomery"],
  ["AK", "Juneau"],
  ["AZ", "Phoenix"],
  ["AR", "Little Rock"],
  ["CA", "Sacramento"],
  ["CO", "Denver"],
  ["CT", "Hartford"],
  ["DE", "Dover"],
  ["FL", "Tallahassee"],
  ["GA", "Atlanta"],
  ["HI", "Honolulu"],
  ["ID", "Boise"],
  ["IL", "Springfield"],
  ["IN", "Indianapolis"],
  ["IA", "Des Moines"],
  ["KS", "Topeka"],
  ["KY", "Frankfort"],
  ["LA", "Baton Rouge"],
  ["ME", "Augusta"],
  ["MD", "Annapolis"],
  ["MA", "Boston"],
  ["MI", "Lansing"],
  ["MN", "Saint Paul"],
  ["MS", "Jackson"],
  ["MO", "Jefferson City"],
  ["MT", "Helena"],
  ["NE", "Lincoln"],
  ["NV", "Carson City"],
  ["NH", "Concord"],
  ["NJ", "Trenton"],
  ["NM", "Santa Fe"],
  ["NY", "Albany"],
  ["NC", "Raleigh"],
  ["ND", "Bismarck"],
  ["OH", "Columbus"],
  ["OK", "Oklahoma City"],
  ["OR", "Salem"],
  ["PA", "Harrisburg"],
  ["RI", "Providence"],
  ["SC", "Columbia"],
  ["SD", "Pierre"],
  ["TN", "Nashville"],
  ["TX", "Austin"],
  ["UT", "Salt Lake City"],
  ["VT", "Montpelier"],
  ["VA", "Richmond"],
  ["WA", "Olympia"],
  ["WV", "Charleston"],
  ["WI", "Madison"],
  ["WY", "Cheyenne"],
]);

const uniqueStrings = (values = []) => {
  const seen = new Set();
  const next = [];

  values.forEach((value) => {
    const label = cleanText(value);
    const key = normalizeKey(label);
    if (!label || seen.has(key)) return;
    seen.add(key);
    next.push(label);
  });

  return next;
};

const ADDRESS_UNIT_HINT_PATTERN = /\b(?:apt|apartment|building|bldg|dept|department|floor|fl|room|rm|suite|ste|unit)\b/i;
const ADDRESS_TRAILING_CITY_PATTERN =
  /\b(?:alley|aly|avenue|ave|boulevard|blvd|circle|cir|court|ct|drive|dr|highway|hwy|lane|ln|parkway|pkwy|place|pl|plaza|plz|road|rd|route|rte|square|sq|street|st|terrace|ter|trail|trl|way)\.?\s+([A-Za-z][A-Za-z .'-]+)$/i;
const US_ZIP_CODE_PATTERN = /\b\d{5}(?:-\d{4})?\b/g;

const flattenJsonLocationInput = (value) => {
  if (Array.isArray(value)) {
    return value.flatMap((item) => flattenJsonLocationInput(item));
  }

  if (value && typeof value === "object") {
    const structuredLabel =
      formatLocationLabel(
        value.city ?? value.location_city,
        value.state ?? value.location_state,
        value.country ?? value.location_country
      ) || "";

    return flattenJsonLocationInput(
      value.label ||
        value.location ||
        structuredLabel ||
        value.city ||
        value.state ||
        value.country ||
        value.location_city ||
        value.location_state ||
        value.location_country ||
        ""
    );
  }

  const text = cleanText(value);
  if (!text) return [];

  const parsed = safeJsonParse(text, null);
  if (Array.isArray(parsed)) {
    return parsed.flatMap((item) => flattenJsonLocationInput(item));
  }

  return [text];
};

const cleanLocationPart = (value) =>
  cleanText(value)
    .replace(/^in\s+/i, "")
    .replace(/[.]+$/g, "")
    .trim();

const stripPostalCodes = (value) =>
  cleanLocationPart(value)
    .replace(US_ZIP_CODE_PATTERN, " ")
    .replace(/\s{2,}/g, " ")
    .replace(/\s+,/g, ",")
    .trim();

const looksLikeStreetAddress = (value) => {
  const cleaned = cleanLocationPart(value);
  if (!cleaned) return false;

  return Boolean(
    /\d/.test(cleaned) ||
      ADDRESS_UNIT_HINT_PATTERN.test(cleaned) ||
      (ADDRESS_TRAILING_CITY_PATTERN.test(cleaned) && cleaned.split(/\s+/).length >= 4)
  );
};

const inferCityFromAddressText = (value) => {
  const cleaned = cleanLocationPart(value);
  if (!cleaned) return "";

  const commaParts = cleaned.split(",").map((part) => cleanLocationPart(part)).filter(Boolean);
  for (let index = commaParts.length - 1; index >= 0; index -= 1) {
    const candidate = commaParts[index];
    if (!candidate || /\d/.test(candidate) || ADDRESS_UNIT_HINT_PATTERN.test(candidate)) continue;
    if (candidate.split(/\s+/).length <= 5) return candidate;
  }

  const streetSuffixMatch = cleaned.match(ADDRESS_TRAILING_CITY_PATTERN);
  if (streetSuffixMatch?.[1] && !/\d/.test(streetSuffixMatch[1])) {
    return cleanLocationPart(streetSuffixMatch[1]);
  }

  const words = cleaned.split(/\s+/).filter(Boolean);
  for (let size = Math.min(words.length, 3); size >= 1; size -= 1) {
    const candidate = cleanLocationPart(words.slice(-size).join(" "));
    if (!candidate || /\d/.test(candidate) || ADDRESS_UNIT_HINT_PATTERN.test(candidate)) continue;
    return candidate;
  }

  return "";
};

const normalizeAliasKey = (value) =>
  cleanLocationPart(value)
    .toLowerCase()
    .replace(/[.]/g, " ")
    .replace(/\s+/g, " ")
    .trim();

const resolveCountryAlias = (value) => COUNTRY_LABEL_BY_KEY.get(normalizeAliasKey(value)) || "";

const normalizeCountry = (value) => {
  const cleaned = cleanLocationPart(value);
  return cleaned ? resolveCountryAlias(cleaned) || cleaned : "";
};

const getCountryDisplayLabel = (value, { preferFullName = false } = {}) => {
  const normalized = normalizeCountry(value);
  if (!normalized) return "";

  if (!preferFullName) return normalized;
  return COUNTRY_FULL_LABEL_BY_KEY.get(normalizeKey(normalized)) || normalized;
};

const normalizeState = (value) => {
  const cleaned = cleanLocationPart(value);
  if (!cleaned) return "";
  // US states: normalize to 2-letter code; Canadian provinces: normalize to full name
  return (
    US_STATE_CODE_BY_KEY.get(normalizeAliasKey(cleaned)) ||
    CA_PROVINCE_FULL_BY_KEY.get(normalizeAliasKey(cleaned)) ||
    cleaned
  );
};

const isRecognizedUsState = (value) => {
  const normalizedState = normalizeState(value);
  return Boolean(
    normalizedState && US_STATE_CODE_BY_KEY.has(normalizeAliasKey(normalizedState))
  );
};

const LOCATION_LIST_NARRATIVE_PATTERNS = [
  /\bmultiple locations\b/i,
  /\blocations?\s+across\b/i,
  /\bincluding\b/i,
  /\blocations?\s*:/i,
  /\bavailable in\b/i,
];

const mergeNarrativeLocationTokens = (values = []) => {
  const merged = [];

  values.forEach((value) => {
    const token = cleanLocationPart(value);
    if (!token) return;

    if (!merged.length) {
      merged.push(token);
      return;
    }

    const normalizedState = normalizeState(token);
    const tokenIsState = Boolean(
      normalizedState && US_STATE_CODE_BY_KEY.has(normalizeAliasKey(normalizedState))
    );
    const tokenIsCountry = Boolean(resolveCountryAlias(token));
    const lastIndex = merged.length - 1;

    if (tokenIsState || tokenIsCountry) {
      merged[lastIndex] = `${merged[lastIndex]}, ${token}`;
      return;
    }

    merged.push(token);
  });

  return merged;
};

const inferCountryFromLocationNarrative = (sourceText, entryText) => {
  const directCountry = resolveCountryAlias(entryText);
  if (directCountry) return directCountry;

  const entryKey = normalizeKey(entryText);
  if (BARE_LOCATION_COUNTRY_HINT_BY_KEY.has(entryKey)) {
    return BARE_LOCATION_COUNTRY_HINT_BY_KEY.get(entryKey) || "";
  }

  const source = cleanText(sourceText);
  if (/\bcanada\b/i.test(source) && !/\b(?:us|usa|united states)\b/i.test(source)) {
    return "Canada";
  }
  if (/\b(?:us|usa|united states)\b/i.test(source) && !/\bcanada\b/i.test(source)) {
    return "USA";
  }
  if (/\b(?:north america|americas)\b/i.test(source)) {
    return "USA";
  }

  return "";
};

const expandNarrativeLocationEntries = (value) => {
  const text = cleanText(value);
  if (!text) return [];

  const looksNarrative = LOCATION_LIST_NARRATIVE_PATTERNS.some((pattern) => pattern.test(text));
  if (!looksNarrative) return [text];

  let listText = text;
  const includingMatch = text.match(/\bincluding\b([\s\S]*)$/i);
  if (includingMatch?.[1]) {
    listText = includingMatch[1];
  } else if (/\blocations?\s*:/i.test(text)) {
    listText = text.replace(/^[\s\S]*?\blocations?\s*:\s*/i, "");
  } else {
    listText = text.replace(
      /^(?:multiple|various|several)\s+locations?(?:\s+across\s+[^,]+)?\s*,?/i,
      ""
    );
  }

  const extractedEntries = mergeNarrativeLocationTokens(
    listText
      .split(/\s*,\s*/g)
      .map((item) => cleanLocationPart(item))
      .filter(Boolean)
  )
    .map((entry) => {
      const normalized = normalizeLocationEntry(entry);
      if (normalized) return normalized;

      const inferredCountry = inferCountryFromLocationNarrative(text, entry);
      return inferredCountry ? formatLocationLabel(entry, "", inferredCountry) : "";
    })
    .filter(Boolean);

  return extractedEntries.length ? extractedEntries : [text];
};

const splitLocationEntries = (value) =>
  cleanText(value)
    .split(/\s*(?:;|\||\n|•)\s*|\s+\/\s+/g)
    .map((item) => cleanText(item))
    .filter(Boolean)
    .flatMap((item) => expandNarrativeLocationEntries(item));

const splitTrailingCountry = (value) => {
  const cleaned = cleanLocationPart(value);
  if (!cleaned) return { remainder: "", country: "" };

  const exactCountry = resolveCountryAlias(cleaned);
  if (exactCountry) {
    return { remainder: "", country: exactCountry };
  }

  const tokens = cleaned.split(/\s+/).filter(Boolean);
  for (let size = Math.min(tokens.length - 1, 5); size >= 1; size -= 1) {
    const suffix = tokens.slice(-size).join(" ");
    const country = resolveCountryAlias(suffix);
    if (!country) continue;

    return {
      remainder: cleanLocationPart(tokens.slice(0, -size).join(" ")),
      country,
    };
  }

  return { remainder: cleaned, country: "" };
};

const isPlausibleLocationEntry = ({ raw = "", city = "", state = "", country = "", label = "" } = {}) => {
  const rawText = cleanText(raw || label);
  if (!rawText) return false;
  if (!hasMinimumLocationLength(rawText)) return false;
  if (rawText.length > 120) return false;
  if (INVALID_LOCATION_PATTERNS.some((pattern) => pattern.test(rawText))) return false;

  const normalizedCity = cleanLocationPart(city);
  const normalizedState = normalizeState(state);
  const normalizedCountry = normalizeCountry(country);
  const commaParts = rawText.split(",").map((part) => cleanLocationPart(part)).filter(Boolean);

  if (normalizedCity && !hasMinimumLocationLength(normalizedCity)) return false;
  if (normalizedState && !hasMinimumLocationLength(normalizedState)) return false;
  if (normalizedCountry && !hasMinimumLocationLength(normalizedCountry)) return false;

  if (!normalizedCity && !normalizedState && !normalizedCountry) return false;
  if (commaParts.length > 3 && !(normalizedCountry && !normalizedCity && !normalizedState)) {
    return false;
  }

  if (normalizedCountry && !normalizedCity && !normalizedState) {
    return true;
  }

  if (normalizedState && normalizedCountry && !normalizedCity) {
    return true;
  }

  if (normalizedCity && normalizedState && normalizedCountry) {
    return true;
  }

  if (normalizedCity && normalizedState && !normalizedCountry) {
    return true;
  }

  if (normalizedCity && !normalizedState && normalizedCountry) {
    return true;
  }

  // Allow user-entered freeform locations such as city-only values when they
  // don't match a known structured location yet.
  if (normalizedCity && !normalizedState && !normalizedCountry) {
    if (isRecognizedUsState(normalizedCity)) return false;
    return /^[A-Za-z][A-Za-z .'-]+$/.test(normalizedCity);
  }

  return false;
};

export const formatLocationLabel = (city, state, country) => {
  const normalizedCity = cleanLocationPart(city);
  const normalizedState = normalizeState(state);
  const normalizedCountry =
    normalizeCountry(country) || (US_STATE_CODE_BY_KEY.has(normalizeAliasKey(normalizedState)) ? "USA" : "");
  const displayCountry =
    normalizedCountry && !normalizedCity && !normalizedState
      ? getCountryDisplayLabel(normalizedCountry, { preferFullName: true })
      : normalizedCountry;

  return uniqueStrings([
    normalizedCity,
    normalizedState,
    displayCountry,
  ]).join(", ");
};

const DEFAULT_US_STATE_LOCATION_OPTIONS = US_STATE_ENTRIES.filter(([code]) => code !== "DC").map(
  ([code]) => formatLocationLabel(US_STATE_DEFAULT_CITY_BY_CODE.get(code) || "", code, "USA")
);

const parseLocationEntry = (value) => {
  const text = cleanText(value);
  if (!hasMinimumLocationLength(text)) {
    return {
      raw: "",
      city: "",
      state: "",
      country: "",
      label: "",
    };
  }

  const parts = text
    .split(",")
    .map((part) => cleanLocationPart(part))
    .filter(Boolean);

  if (!parts.length) {
    return {
      raw: text,
      city: "",
      state: "",
      country: "",
      label: "",
    };
  }

  let city = parts[0] || "";
  let tail = parts.slice(1);
  let country = "";

  if (tail.length) {
    const trailingCountry = splitTrailingCountry(tail[tail.length - 1]);
    if (trailingCountry.country) {
      country = trailingCountry.country;
      if (trailingCountry.remainder) tail[tail.length - 1] = trailingCountry.remainder;
      else tail = tail.slice(0, -1);
    }
  } else {
    const trailingCountry = splitTrailingCountry(city);
    if (trailingCountry.country) {
      city = trailingCountry.remainder || "";
      country = trailingCountry.country;
    }
  }

  // "CountryA, CountryB" pattern (e.g. "United Kingdom, Ireland") — two country names
  // concatenated, not a city+country. Return invalid so each is handled as a standalone entry.
  if (city && country && tail.length === 0 && parts.length === 2) {
    const cityAsCountry = resolveCountryAlias(city);
    if (cityAsCountry && normalizeKey(cityAsCountry) !== normalizeKey(country)) {
      return { raw: text, city: "", state: "", country: "", label: "" };
    }
  }

  let state = stripPostalCodes(tail.join(", "));
  state = normalizeState(state);

  if (!state && !tail.length && country) {
    const standaloneState = normalizeState(city);
    if (
      US_STATE_CODE_BY_KEY.has(normalizeAliasKey(standaloneState)) ||
      CA_PROVINCE_FULL_BY_KEY.has(normalizeAliasKey(city))
    ) {
      state = standaloneState;
      city = "";
    }
  }

  // Detect standalone Canadian province code/name even without a country (e.g. bare "ON")
  if (!state && city && CA_PROVINCE_FULL_BY_KEY.has(normalizeAliasKey(city))) {
    state = CA_PROVINCE_FULL_BY_KEY.get(normalizeAliasKey(city));
    city = "";
  }

  if (!state && city) {
    const cityTokens = city.split(/\s+/).filter(Boolean);
    if (cityTokens.length >= 2) {
      const maybeState = normalizeState(cityTokens[cityTokens.length - 1]);
      if (
        US_STATE_CODE_BY_KEY.has(normalizeAliasKey(maybeState)) ||
        CA_PROVINCE_FULL_BY_KEY.has(normalizeAliasKey(cityTokens[cityTokens.length - 1]))
      ) {
        state = maybeState;
        city = cleanLocationPart(cityTokens.slice(0, -1).join(" "));
      }
    }
  }

  if (looksLikeStreetAddress(city)) {
    const inferredCity = inferCityFromAddressText(city);
    if (inferredCity) {
      city = inferredCity;
    }
  }

  const normalizedCountry =
    normalizeCountry(country) || (US_STATE_CODE_BY_KEY.has(normalizeAliasKey(state)) ? "USA" : "");
  const label = formatLocationLabel(city, state, normalizedCountry) || text;
  const parsed = {
    raw: text,
    city: cleanLocationPart(city),
    state,
    country: normalizedCountry,
    label,
  };

  if (!isPlausibleLocationEntry(parsed)) {
    return {
      raw: text,
      city: "",
      state: "",
      country: "",
      label: "",
    };
  }

  return parsed;
};

const getLocationSpecificity = (parsed = {}) =>
  (parsed.city ? 2 : 0) + (parsed.state ? 4 : 0) + (parsed.country ? 1 : 0);

const isParentLocationEntry = (candidate = {}, other = {}) => {
  if (!candidate?.label || !other?.label) return false;
  if (normalizeKey(candidate.label) === normalizeKey(other.label)) return false;

  const sameCountry = partsMatch(candidate.country, other.country);
  const sameState = partsMatch(candidate.state, other.state);

  if (candidate.country && !candidate.state && !candidate.city) {
    return sameCountry && Boolean(other.state || other.city);
  }

  if (candidate.state && !candidate.city) {
    return sameState && (!candidate.country || sameCountry) && Boolean(other.city);
  }

  return false;
};

const areEquivalentLocationEntries = (left, right) => {
  if (!left?.label || !right?.label) return false;
  if (normalizeKey(left.label) === normalizeKey(right.label)) return true;

  const sameCountry = Boolean(
    left.country && right.country && normalizeKey(left.country) === normalizeKey(right.country)
  );
  const sameCity = Boolean(left.city && right.city && normalizeKey(left.city) === normalizeKey(right.city));
  const sameState = Boolean(
    left.state && right.state && normalizeKey(left.state) === normalizeKey(right.state)
  );

  if (
    sameCity &&
    ((!left.state && !left.country && (right.state || right.country)) ||
      (!right.state && !right.country && (left.state || left.country)))
  ) {
    return true;
  }

  if (sameCity && sameCountry && (sameState || !left.state || !right.state)) {
    return true;
  }

  return false;
};

const dedupeLocationLabels = (values = []) => {
  const chosen = [];

  values.forEach((value) => {
    const parsed = parseLocationEntry(value);
    if (!parsed.label) return;

    const matchIndex = chosen.findIndex((existing) => areEquivalentLocationEntries(existing, parsed));
    if (matchIndex < 0) {
      chosen.push(parsed);
      return;
    }

    if (getLocationSpecificity(parsed) > getLocationSpecificity(chosen[matchIndex])) {
      chosen[matchIndex] = parsed;
    }
  });

  return chosen
    .filter(
      (entry, index, all) =>
        !all.some(
          (other, otherIndex) =>
            otherIndex !== index &&
            getLocationSpecificity(other) > getLocationSpecificity(entry) &&
            isParentLocationEntry(entry, other)
        )
    )
    .map((item) => item.label);
};

export const normalizeLocationEntry = (value) => parseLocationEntry(value).label;

const getLocationEntryMeta = (value) => {
  const parsed = parseLocationEntry(value);
  const groupLabel = parsed.country
    ? getCountryDisplayLabel(parsed.country, { preferFullName: true })
    : OTHER_LOCATION_GROUP_LABEL;

  return {
    ...parsed,
    groupLabel,
    sortGroupKey:
      groupLabel === OTHER_LOCATION_GROUP_LABEL
        ? "\uffff"
        : normalizeKey(groupLabel),
    sortLabelKey: normalizeKey(parsed.label),
  };
};

const sortLocationLabels = (values = []) =>
  dedupeLocationLabels(values).sort((left, right) => {
    const leftMeta = getLocationEntryMeta(left);
    const rightMeta = getLocationEntryMeta(right);

    if (leftMeta.sortGroupKey !== rightMeta.sortGroupKey) {
      return leftMeta.sortGroupKey.localeCompare(rightMeta.sortGroupKey);
    }

    return leftMeta.sortLabelKey.localeCompare(rightMeta.sortLabelKey);
  });

const toFlatLocationValues = (value) => {
  if (Array.isArray(value)) {
    return value.flatMap((item) => toFlatLocationValues(item));
  }

  return flattenJsonLocationInput(value);
};

export const normalizeLocationFilterValues = (values = []) =>
  sortLocationLabels(
    toFlatLocationValues(values)
      .flatMap((value) => splitLocationEntries(value))
      .map((value) => normalizeLocationEntry(value))
      .filter(Boolean)
  );

// Read every location representation produced by older list endpoints and the
// current detail endpoints. Do not use `??` between these fields: an empty
// `locations` array must not hide a populated persisted `locations_json` list.
export const getEntityLocationValues = (entity = {}) => {
  const row = entity && typeof entity === "object" ? entity : {};
  const raw = row?.raw && typeof row.raw === "object" ? row.raw : {};
  const structuredLocation = formatLocationLabel(
    row.location_city ?? raw.location_city,
    row.location_state ?? raw.location_state,
    row.location_country ?? raw.location_country
  );

  return normalizeLocationFilterValues([
    row.locations,
    raw.locations,
    row.locations_json,
    raw.locations_json,
    row.locationsJson,
    raw.locationsJson,
    row.location_values,
    raw.location_values,
    structuredLocation,
    row.location,
    raw.location,
  ]);
};

export const normalizeLocationValueForSave = (value) => {
  const raw = cleanText(value);
  if (!hasMinimumLocationLength(raw)) return "";

  return normalizeLocationEntry(raw);
};

export const normalizeLocationValuesForSave = (values = []) =>
  uniqueStrings(
    toFlatLocationValues(values)
      .flatMap((value) => splitLocationEntries(value))
      .map((value) => normalizeLocationValueForSave(value))
      .filter(Boolean)
  );

export const extractRowLocations = (row = {}) => {
  const sourceRow = row?.raw && typeof row.raw === "object" ? row.raw : row;

  return dedupeLocationLabels([
    ...getEntityLocationValues(row),
    ...normalizeLocationFilterValues([
      row?.company_location,
      sourceRow?.company_location,
      row?.current_location,
      sourceRow?.current_location,
      row?.candidate_location,
      sourceRow?.candidate_location,
      row?.pref_locations,
      sourceRow?.pref_locations,
    ]),
  ]);
};

export const buildLocationOptions = (rows = []) => {
  const allOptions = sortLocationLabels([
    ...DEFAULT_US_STATE_LOCATION_OPTIONS,
    ...(Array.isArray(rows) ? rows : []).flatMap((row) => extractRowLocations(row)),
  ]);
  // Only keep entries that have a city AND a country — enforce "City, State, Country" format
  return allOptions.filter((value) => {
    const entry = parseLocationEntry(value);
    return Boolean(entry.city) && Boolean(entry.country);
  });
};

export const groupLocationOption = (value) => getLocationEntryMeta(value).groupLabel;

const partsMatch = (left, right) => Boolean(left && right && normalizeKey(left) === normalizeKey(right));

const locationsSemanticallyMatch = (rowLocation, selectedLocation) => {
  if (!rowLocation?.label || !selectedLocation?.label) return false;
  if (partsMatch(rowLocation.label, selectedLocation.label)) return true;

  const sameCountry = partsMatch(rowLocation.country, selectedLocation.country);
  const sameState = partsMatch(rowLocation.state, selectedLocation.state);
  const sameCity = partsMatch(rowLocation.city, selectedLocation.city);

  if (selectedLocation.country && !selectedLocation.state && !selectedLocation.city) {
    return sameCountry;
  }

  if (selectedLocation.state && !selectedLocation.city) {
    return sameState && (!selectedLocation.country || sameCountry);
  }

  if (selectedLocation.city && selectedLocation.state) {
    if (sameCity && sameState && (!selectedLocation.country || sameCountry)) {
      return true;
    }

    return sameCity && sameCountry && (!rowLocation.state || !selectedLocation.state);
  }

  if (selectedLocation.city && !selectedLocation.state && selectedLocation.country) {
    return sameCity && sameCountry;
  }

  if (selectedLocation.city && !selectedLocation.state && !selectedLocation.country) {
    return sameCity;
  }

  const rowLabelKey = normalizeKey(rowLocation.label);
  const selectedLabelKey = normalizeKey(selectedLocation.label);
  return rowLabelKey.includes(selectedLabelKey) || selectedLabelKey.includes(rowLabelKey);
};

export const matchesLocationFilters = (row = {}, selectedLocations = []) => {
  const normalizedSelected = normalizeLocationFilterValues(selectedLocations).map((value) =>
    parseLocationEntry(value)
  );
  if (!normalizedSelected.length) return true;

  const rowLocations = extractRowLocations(row).map((value) => parseLocationEntry(value));
  if (!rowLocations.length) return false;

  return normalizedSelected.some((selected) =>
    rowLocations.some(
      (location) => locationsSemanticallyMatch(location, selected)
    )
  );
};
